// Surge aura v3 (?aura=v3, owner 2026-11-02: "why does the aura look so cheap?"). Original design built from general
// cel-animation FX craft, no texture and no copied shapes:
//  - silhouette: an analytic flame FIELD per pixel (signed horizontal margin): a body-hugging teardrop widest around the
//    hips/shoulders plus 9 pointed tongues that grow upward, bend at the tip and snap, and small wisps that peel off the
//    top and fly up. The field is posterised into hard bands: thin dark edge (constant ~2 px) -> saturated outer
//    colour -> lighter level colour -> white-hot core hugging the body, with upward-moving chevron notches between bands.
//  - motion: the flame clock is advanced on the CPU but only handed to the GPU 12 times a second (held poses, "on
//    twos"); speed lines step at 24 fps, electricity re-draws at 15 fps; a sharp-attack throb pulses the width.
//  - layers: back card (opaque-looking, behind the hero so his body stays readable), thinner front card (edge + tongues,
//    the body column nearly clear), ground layer (lit pool with a flame edge, outward pressure streaks = flattened
//    grass, dust rings, ignition shock + dust blast), lifting pebbles, dust puffs, rising speed lines, GPU lightning
//    (Crimson a few, Azure dense), ignition / level-up flash. Each layer is ONE draw call; everything is driven by
//    uniforms + `time` (no per-frame allocation, no per-particle CPU work).
//  - SURGE FORMS IV-VIII (owner 2026-11-03): the looks for tiers 4..8 derive from src/core/surgeForms.ts (v3FormLook);
//    every size, rate and count scales with the form's intensity, and each form's motif switches on one extra layer:
//    lightning (emerald bolts that lash out), eclipse (a dark corona ring with a bent-light rim, one billboard quad),
//    inferno (black tongues + molten core, heat shimmer in the cards, glowing ground cracks), abyss (VII Abyssal
//    Singularity, the Singularity orb's look on the aura: a pitch-black body, a thick glowing crimson rim + soft outer
//    halo carrying the silhouette on any map, slow red veins drifting through the black, the densest red-white bolts), shards (orbiting crystals, fracture lines in the air, a rising-debris ring). Every extra layer is one
//    draw call, pooled and driven by uniforms; a form that doesn't use it leaves it empty (count 0 / hidden).
// Render-only: reads the smoothed Surge level the view already passes to SurgeVisual; never feeds gameplay.
import * as THREE from 'three/webgpu';
import {
  abs, atan, attribute, cameraProjectionMatrix, cameraWorldMatrix, clamp, cos, cross, float, floor, fract, fwidth,
  length, max, min, mix, mod, modelViewMatrix, modelWorldMatrixInverse, normalize, normalLocal, oneMinus, positionLocal,
  pow, sin, smoothstep, step, time, uniform, uv, varying, vec2, vec3, vec4,
} from 'three/tsl';
import { AURA_SOLE, M } from './auraFlame';
import { FLASH } from './a11y';
import { SURGE_FORMS, type SurgeForm } from '../core/surgeForms';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type U = any;
const V3: U = vec3, V4: U = vec4;
const TAU = Math.PI * 2;
/** shader hash; arguments are kept small (cycle counters wrap) so fp32 stays random-looking */
const hash = (x: U): U => fract(sin(x.mul(12.9898).add(0.731)).mul(43758.5453));
const jsHash = (x: number) => { const s = Math.sin(x * 12.9898 + 0.731) * 43758.5453; return s - Math.floor(s); };
const FEET = vec3(0, AURA_SOLE, 0);
const vary = (x: U, name: string): U => varying(x, name);

/** per-level look (index 0 = base: hidden). Colours are linear-managed hex; sizes in metres. */
export interface V3Look {
  line: number; outer: number; mid: number; core: number;
  /** tongue-top height, max half width, flame clock rate, tongue width factor (lower = sharper), tip sway */
  H: number; W: number; rate: number; sharp: number; wild: number;
  /** speed lines / bolts alive (0..1 of the pool), bolt duty cycle, pebbles, dust puffs (0..1), ground strength, throb */
  lines: number; bolts: number; duty: number; rocks: number; dust: number; ground: number; throb: number;
  /** emission with Glow on / off */
  emitOn: number; emitOff: number;
  /** motif extras (0 = off): bolt colour, lightning lash-out reach, eclipse corona, abyss rim (thick glowing outline + halo
   *  + veins), inferno heat shimmer, ground cracks, orbiting crystals (0..1 of the pool), air fractures (0..1), rocks as a
   *  rising ring */
  bolt: number; lash: number; corona: number; rim: number; heat: number; cracks: number; crystals: number; fract: number; ringK: number;
  /** glow colours of the additive layers (bolt centres, speed lines, shock, flash): white-hot centre and main glow;
   *  default core / mid (VII's core and mid are near-black, so its glow is the red rim) */
  hot?: number; glow?: number;
  /** ignition / level-up burst scale (shock radius, flash size, dust blast) */
  burst: number;
}
const NO_EXTRA = { lash: 0, corona: 0, rim: 0, heat: 0, cracks: 0, crystals: 0, fract: 0, ringK: 0 };
export const V3_LOOKS: V3Look[] = [
  { line: 0x0a1a40, outer: 0x2a6ab0, mid: 0x6cc4ff, core: 0xeaf8ff, H: 2.2, W: 0.7, rate: 0.8, sharp: 1, wild: 0.2, lines: 0, bolts: 0, duty: 0, rocks: 0, dust: 0, ground: 0, throb: 0.03, emitOn: 1.2, emitOff: 1, bolt: 0x6cc4ff, ...NO_EXTRA, burst: 0.8 },
  // I Radiant: clean gold flame
  { line: 0x5a1e00, outer: 0xf57200, mid: 0xffb414, core: 0xfff6d2, H: 3.1, W: 0.95, rate: 1.0, sharp: 1.0, wild: 0.22, lines: 0.45, bolts: 0, duty: 0, rocks: 0.35, dust: 0.5, ground: 0.75, throb: 0.05, emitOn: 1.25, emitOff: 1, bolt: 0xffb414, ...NO_EXTRA, burst: 0.85 },
  // II Crimson: taller, sharper, more aggressive, a few sparks of electricity
  { line: 0x3a000c, outer: 0xc40c2a, mid: 0xff3c2e, core: 0xffe6dc, H: 3.6, W: 1.05, rate: 1.35, sharp: 0.8, wild: 0.36, lines: 0.7, bolts: 0.35, duty: 0.3, rocks: 0.65, dust: 0.75, ground: 0.9, throb: 0.07, emitOn: 1.3, emitOff: 1, bolt: 0xff3c2e, ...NO_EXTRA, burst: 0.95 },
  // III Azure: tallest, densest, fastest, constant crackling electricity, strongest ground reaction
  { line: 0x06144e, outer: 0x1a52f0, mid: 0x2ec4ff, core: 0xeefcff, H: 4.1, W: 1.15, rate: 1.75, sharp: 0.68, wild: 0.46, lines: 1, bolts: 1, duty: 0.7, rocks: 1, dust: 1, ground: 1, throb: 0.09, emitOn: 1.35, emitOff: 1, bolt: 0x2ec4ff, ...NO_EXTRA, burst: 1.05 },
];
const _c = new THREE.Color();
const shade = (hex: number, k: number): number => _c.setHex(hex).multiplyScalar(k).getHex();
/** the v3 look of a Surge form from the data contract. Owner 2026-11-03: colours match (every form's line / outer / mid /
 *  core / bolt come from surgeForms.ts, the palette the HUD uses), every aura bigger and more intense than before (I is
 *  already taller and brighter than the old Radiant; VIII's tongues top out ~2.5x the hero), faster flicker, more lines,
 *  rocks and dust, and GPU bolts on every form (lash-out reach grows with the form, IV's storm the densest). */
export function v3FormLook(f: SurgeForm): V3Look {
  const s = Math.max(0, Math.min(1, f.intensity)), L = (a: number, b: number) => a + (b - a) * s, m = f.motif;
  const ab = m === 'abyss';
  return {
    line: m === 'inferno' ? 0x050204 : ab ? f.accent : m === 'shards' ? shade(f.accent, 0.45) : shade(f.outer, 0.3), outer: f.outer, mid: f.inner, core: f.core,
    H: L(3.6, 5.6), W: L(1.05, 1.7), rate: L(1.3, 3), sharp: L(1, 0.5), wild: L(0.25, 0.75),
    lines: L(0.6, 1), bolts: m === 'lightning' || ab ? 1 : L(0.3, 1), duty: m === 'lightning' ? 0.9 : ab ? 0.95 : L(0.25, 0.8),
    rocks: L(0.5, 1), dust: L(0.6, 1), ground: L(0.8, 1), throb: L(0.06, 0.15), emitOn: L(1.4, 1.9), emitOff: L(1, 1.15),
    bolt: f.accent, lash: m === 'lightning' ? 1 : ab ? 0.5 : L(0.1, 0.7), corona: m === 'eclipse' ? 1 : 0, rim: ab ? 1 : 0, heat: m === 'inferno' ? 1 : 0,
    cracks: m === 'inferno' ? 1 : m === 'shards' ? 0.45 : 0, crystals: m === 'shards' ? 1 : 0, fract: m === 'shards' ? 1 : 0, ringK: m === 'shards' ? 1 : 0,
    burst: L(0.9, 1.9),
    ...(ab ? { hot: 0xffc0c8, glow: f.accent } : {}),
  };
}
for (const f of SURGE_FORMS) V3_LOOKS[f.tier] = v3FormLook(f);
const V3_TOP = V3_LOOKS.length - 1;

const ROCKS = 16, DUST = 10, DUST_BURST = 12, LINES = 28, BOLTS = 10, BOLT_SEGS = 6, CRYSTALS = 14, FRACTS = 14;
/** card field space: u in [-UX, UX] (1 = max half width), v in [-V0, VR - V0] (1 = tallest tongue tip) */
const UX = 1.4, VR = 1.38, V0 = 0.06;

const transparent = (depthTest = true) => ({ m: new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, depthTest, side: THREE.DoubleSide, fog: false }) });

/** camera frame in model space: horizontal right/back (cylindrical billboard), full back and up */
function camFrame(): { right: U; back: U; fullBack: U; up: U } {
  const inv: U = modelWorldMatrixInverse, cam: U = cameraWorldMatrix;
  const camBack = inv.mul(cam.mul(vec4(0, 0, 1, 0))).xyz;
  const back: U = normalize(V3(camBack.x, 0, camBack.z));
  const right: U = normalize(cross(vec3(0, 1, 0), back));
  const up: U = normalize(inv.mul(cam.mul(vec4(0, 1, 0, 0))).xyz);
  return { right, back, fullBack: normalize(camBack), up };
}
/** a world-space horizontal direction expressed in model space (so ground effects don't spin when the hero turns) */
const worldDir = (th: U): U => normalize((modelWorldMatrixInverse as U).mul(V4(cos(th), 0, sin(th), 0)).xyz);

export class AuraV3 {
  readonly group = new THREE.Group();
  readonly back: THREE.Mesh; readonly front: THREE.Mesh | null; readonly ground: THREE.Mesh; readonly rocksMesh: THREE.Mesh;
  readonly dustMesh: THREE.Mesh; readonly lines: THREE.Mesh; readonly bolts: THREE.Mesh; readonly flash: THREE.Mesh;
  /** motif layers: eclipse corona ring, orbiting crystals, air fractures */
  readonly corona: THREE.Mesh; readonly crystals: THREE.Mesh; readonly fracts: THREE.Mesh;
  // ---- uniforms (scalars/colours only; written once per frame)
  readonly cLine = uniform(new THREE.Color()); readonly cOuter = uniform(new THREE.Color()); readonly cCore = uniform(new THREE.Color());
  /** bolt / crystal / fracture accent colour */
  readonly cBolt = uniform(new THREE.Color());
  /** glow colours of the additive layers (V3Look.hot / glow; default core / mid) */
  readonly cHot = uniform(new THREE.Color()); readonly cGlow = uniform(new THREE.Color());
  /** VII abyss rim strength (0 = every other form) */
  readonly rimK = uniform(0);
  /** WRAP-AROUND RIM LIGHT (owner 2026-11-03, every form): crisp bright rim band strength / width just inside the cel
   *  edge, a fresnel-style falloff wrapping the light inward, and a soft halo following the outline (strength / reach in
   *  field units); all scale with the form's intensity (I subtle .. VIII big) */
  readonly wrapK = uniform(0); readonly wrapW = uniform(0.03); readonly haloK = uniform(0); readonly haloW = uniform(0.06);
  /** motif strengths (see V3Look) */
  readonly lash = uniform(0); readonly coronaK = uniform(0); readonly heat = uniform(0); readonly cracks = uniform(0);
  readonly nCrystals = uniform(0); readonly nFract = uniform(0); readonly ringK = uniform(0);
  /** burst scale: shock-ring radius, flash size and dust blast reach grow with the form's intensity */
  readonly burstR = uniform(1);
  readonly emit = uniform(1); readonly alpha = uniform(0);
  readonly H = uniform(3); readonly W = uniform(1); readonly grow = uniform(1); readonly throb = uniform(1);
  /** held flame clock (updated 12x per second) */
  readonly T = uniform(0);
  readonly sharp = uniform(1); readonly wild = uniform(0.3);
  readonly nLines = uniform(0); readonly nBolts = uniform(0); readonly duty = uniform(0); readonly nRocks = uniform(0); readonly nDust = uniform(0);
  readonly groundK = uniform(0); readonly groundS = uniform(3);
  /** seconds since the last ignition / level-up (9 = idle) and its strength (1 ignition, 0.7 level-up) */
  readonly burst = uniform(9); readonly burstK = uniform(1);
  /** gameplay-camera height factor (the steep camera shortens nothing on screen; kept as a QA knob) */
  readonly gameH = uniform(Number(new URLSearchParams(location.search).get('auragh')) || 1.12);
  readonly gameW = uniform(Number(new URLSearchParams(location.search).get('auragw')) || 1.3);
  private clock = 0; private holdAcc = 0; private lvl = 0; private lv = 0; private burstT = 9; private burstMode = 0;
  /** the form's intensity (0..1) the view can read for its own escalation (shake, hitstop, ground) */
  intensity = 0;
  /** a11y: the ignition / level-up flare's share of the flash budget (taken when it fires) */
  private burstGain = 1;

  /** `mid` is SurgeVisual.auraColor (shared so the Overdrive tint still applies) */
  constructor(private readonly cMid: U, quality: [number, number, number, boolean]) {
    this.ground = this.buildGround();
    this.back = this.buildCard(false);
    this.front = quality[3] ? this.buildCard(true) : null;
    this.rocksMesh = this.buildRocks();
    this.dustMesh = this.buildDust();
    this.lines = this.buildLines();
    this.bolts = this.buildBolts();
    this.flash = this.buildFlash();
    this.corona = this.buildCorona(); this.crystals = this.buildCrystals(); this.fracts = this.buildFracts();
    this.group.add(this.ground, this.back, this.rocksMesh, this.dustMesh, this.lines, this.bolts, this.flash, this.corona, this.crystals, this.fracts);
    if (this.front) this.group.add(this.front);
    this.qp = quality[0];
  }
  private qp = 1;

  // ------------------------------------------------------------------ flame field
  /** signed horizontal margin of the flame silhouette at (u, v); `n` tongues, `wisps` detached tongues */
  private field(u: U, v: U, n: number, seed: number, wisps: number): U {
    const T = this.T, au = abs(u);
    // body teardrop: narrow at the feet, widest around hips/shoulders, domed to a point at v ~ 0.8
    const rise = smoothstep(-0.04, 0.24, v);
    const t2 = clamp(v.sub(0.24).div(0.44), 0, 1);
    // side licks: a rising sawtooth on each flank - the width creeps out then cuts back in hard, so the flanks are a
    // stack of upward-hooked points that climb with the flow (left/right out of phase)
    const sideP = v.mul(4.4).sub(au.mul(2.6)).sub(T.mul(2.3)).add(step(0, u).mul(0.5)); // cut slants up-and-out: hooks point up
    const lick = pow(fract(sideP.add(hash(floor(sideP)).mul(0.35))), 2.6).mul(0.2).mul(smoothstep(0.12, 0.3, v));
    const E = mix(0.3, 0.92, rise).add(lick).mul(pow(oneMinus(pow(t2, 1.5)), 0.65));
    let d: U = min(E.sub(au), v.add(0.02).sub(au.mul(au).mul(0.3)).mul(2.4)); // rounded teardrop base
    for (let i = 0; i < n; i++) {
      const s = (i + 0.5) / n, x0 = (s * 2 - 1) * 0.9, edge = Math.abs(x0);
      const L = 0.36 + 0.22 * jsHash(i * 1.618 + seed); // flame-clock seconds per tongue life
      const ph = T.div(L).add(jsHash(i * 3.1 + seed * 0.7) * 7);
      const p = fract(ph), cyc = mod(floor(ph), 241);
      const r1 = hash(cyc.mul(1.37).add(i * 7.31 + seed)), r2 = hash(cyc.mul(2.11).add(i * 3.17 + seed * 1.9));
      const vb = float(0.4 - 0.26 * edge).add(p.mul(0.1)); // base drifts up with the flow
      const top = float(1.03 - 0.3 * edge).mul(mix(0.8, 1.05, r1));
      const g = smoothstep(0.0, 0.32, p), snap = smoothstep(0.74, 0.8, p); // stretch up, then the tip snaps off
      const h = vb.add(top.sub(vb).mul(g).mul(oneMinus(snap.mul(0.5))));
      const sN = clamp(v.sub(vb).div(max(h.sub(vb), 0.02)), 0, 1);
      const tw = this.sharp.mul(0.26 - 0.08 * edge).mul(pow(oneMinus(sN), 1.1)).mul(mix(0.55, 1, g));
      // tongues lean in toward the top and their tips whip sideways
      const cx = float(x0).mul(oneMinus(sN.mul(0.5))).add(r2.sub(0.5).mul(this.wild).mul(sN.mul(sN)).mul(2));
      const di = min(tw.sub(abs(u.sub(cx))), v.sub(vb).add(0.14).mul(3));
      d = max(d, di);
    }
    for (let j = 0; j < wisps; j++) {
      const L = 0.5 + 0.15 * jsHash(j * 5.3 + seed);
      const ph = T.div(L).add(jsHash(j * 2.9 + seed) * 5);
      const p = fract(ph), cyc = mod(floor(ph), 241);
      const x = hash(cyc.mul(1.7).add(j * 5.3 + seed)).sub(0.5).mul(0.9);
      const y0 = p.mul(0.5).add(0.74);
      const sz = oneMinus(p.mul(0.6)).mul(0.14).mul(step(p, 0.82)).mul(this.sharp.add(0.3).mul(0.77));
      const sN = clamp(v.sub(y0).div(max(sz.mul(3.4), 0.01)), 0, 1);
      const tw = sz.mul(pow(sin(sN.mul(Math.PI)), 0.6)).mul(pow(oneMinus(sN), 0.4));
      d = max(d, tw.sub(abs(u.sub(x).sub(sN.mul(sN).mul(0.05)))));
    }
    return d;
  }

  /** cel bands from the field: returns [colour, inside, mid-band mask] */
  private bands(d: U, u: U, v: U): [U, U, U] {
    const fw = max(fwidth(d), 0.0005);
    const inside = smoothstep(fw.negate(), fw, d);
    const lineW = max(this.rimK.mul(0.085).add(0.026), fw.mul(1.3)); // dark edge: >= ~1.3 px at any distance (VII: a thick rim)
    const kO = smoothstep(lineW.sub(fw), lineW.add(fw), d);
    // VII abyss rim (the Singularity orb's crimson event-horizon rim on the flame): hottest at the silhouette, cooling to
    // deep red where it meets the black body; outside the silhouette (d < 0) it is the halo's colour
    const rimT = clamp(d.div(lineW), 0, 1);
    const rimCol = mix(this.cHot.mul(this.emit.mul(1.3)), this.cGlow.mul(this.emit.mul(1.5)), smoothstep(0, 0.35, rimT)).mul(oneMinus(smoothstep(0.55, 1, rimT).mul(0.45)));
    const edgeCol = mix(this.cLine, rimCol, this.rimK);
    // chevron notches flowing up through the band boundaries
    const f1 = fract(v.mul(2.2).add(abs(u).mul(1.8)).sub(this.T.mul(1.9)));
    const f2 = fract(v.mul(2.8).add(abs(u).mul(1.5)).sub(this.T.mul(2.6)).add(0.37));
    const mT = pow(f1, 3).mul(0.06).add(0.17);
    const cT = pow(f2, 3).mul(0.05).add(0.46).add(smoothstep(0.28, 0.6, v).mul(0.5)); // white-hot only low, hugging the body
    const kM = smoothstep(mT.sub(fw), mT.add(fw), d), kC = smoothstep(cT.sub(fw), cT.add(fw), d);
    let col: U = mix(edgeCol, this.cOuter, kO);
    col = mix(col, this.cMid.mul(this.emit), kM);
    col = mix(col, this.cCore.mul(this.emit.mul(1.35)), kC);
    // WRAP-AROUND RIM LIGHT (owner 2026-11-03, the Nova Lance / Singularity treatment on every aura): the flame card is
    // shaded as if it were a curved volume. A crisp, saturated, bright rim band sits just inside the thin cel edge (the
    // hardest, most saturated line of the silhouette), then a fresnel-style falloff carries the form's glow inward from
    // the outline, stronger on the key-light side (screen right) so the light reads as wrapping round the shape. The
    // back card adds a soft halo outside the outline (buildCard). Shader-only: no extra pass, no light.
    const dIn = d.sub(lineW);
    const rimBand = kO.mul(oneMinus(smoothstep(this.wrapW.sub(fw), this.wrapW.add(fw), dIn)));
    const fres = kO.mul(pow(oneMinus(clamp(dIn.div(0.22), 0, 1)), 2.5));
    const keySide = clamp(u.mul(0.9).add(0.55), 0.25, 1);
    const rimLight = mix(this.cOuter, this.cGlow, 0.45).mul(this.emit.mul(1.75));
    col = mix(col, rimLight, rimBand.mul(this.wrapK).mul(oneMinus(this.rimK)).mul(oneMinus(kC)));
    col = col.add(this.cGlow.mul(this.emit).mul(fres.mul(keySide).mul(this.wrapK).mul(0.38)).mul(oneMinus(kC)).mul(oneMinus(this.rimK.mul(0.7)))); // VII keeps its body black
    // VII: thin red veins drifting up through the black body (slow, like the orb's swirl; 0 for every other form)
    const vq = v.mul(7).sub(this.T.mul(1.1)).add(sin(u.mul(5.3).add(this.T.mul(0.7))).mul(0.9)).add(abs(u).mul(2.2));
    const vein = pow(abs(sin(vq.mul(Math.PI))), 70).mul(kO).mul(oneMinus(kC.mul(0.5))).mul(smoothstep(0.05, 0.3, v));
    col = col.add(this.cGlow.mul(this.emit.mul(0.45)).mul(vein.mul(this.rimK)));
    return [col, inside, kM];
  }

  // ------------------------------------------------------------------ 1/2. back + front flame cards
  private buildCard(isFront: boolean): THREE.Mesh {
    const { m } = transparent();
    const { right, back, up } = camFrame();
    const p: U = positionLocal;
    // lean the card's up axis toward the camera's screen-up (the steep gameplay camera would squash a vertical card
    // into a short blob); at the gameplay camera the height is trimmed by gameH so the aura stays ~2x the hero
    const lean = isFront ? 0.35 : 0.6;
    const A: U = normalize(mix(vec3(0, 1, 0), up, lean));
    const gameT = oneMinus(smoothstep(0.62, 0.95, up.y)); // 1 at the steep gameplay camera, 0 at eye level
    const Hc = this.H.mul(this.grow).mul(mix(1, this.gameH, gameT)).mul(isFront ? 0.86 : 1);
    const Wc = this.W.mul(mix(0.55, 1, min(this.grow, 1))).mul(mix(1, this.gameW, gameT)).mul(isFront ? 0.92 : 1);
    const off = isFront ? 0.3 : -0.22; // metres toward (front) / away from (back) the camera
    const P = FEET.add(back.mul(off * M)).add(right.mul(p.x.mul(2 * UX).mul(Wc).mul(M))).add(A.mul(p.y.add(0.5).mul(VR).sub(V0).mul(Hc).mul(M)));
    m.vertexNode = cameraProjectionMatrix.mul(modelViewMatrix.mul(V4(P, 1)));
    const v = uv().y.mul(VR).sub(V0);
    // VII inferno: heat shimmer, the field's u wobbles with height (0 for every other form; same shader, one uniform)
    const u = uv().x.mul(2).sub(1).mul(UX).div(this.throb).add(sin(v.mul(9).add(this.T.mul(7))).mul(this.heat).mul(0.045));
    const d = this.field(u, v, isFront ? 7 : 9, isFront ? 11.3 : 0, isFront ? 1 : 3);
    const [col, inside, kM] = this.bands(d, u, v);
    m.colorNode = isFront ? col : mix(this.cGlow.mul(this.emit.mul(1.2)), col, inside); // back card: halo outside the outline
    if (isFront) {
      // in front of the hero: edge + outer band + tongues; the interior is a faint veil and nearly clear over the body
      const bodyCol = oneMinus(smoothstep(0.26, 0.4, abs(u))).mul(oneMinus(smoothstep(0.5, 0.64, v)));
      m.opacityNode = inside.mul(this.alpha).mul(mix(0.92, 0.2, kM)).mul(oneMinus(bodyCol.mul(0.8)));
    } else {
      // soft bloom halo hugging the outline in the form's glow colour (every form; VII's is the strongest, crimson, so the
      // black aura still reads on dark ground)
      const halo = pow(smoothstep(this.haloW.negate(), 0, d), 2).mul(oneMinus(inside)).mul(this.haloK);
      m.opacityNode = max(inside.mul(0.97), halo).mul(this.alpha);
    }
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1, 1, 1), m);
    mesh.frustumCulled = false; mesh.renderOrder = isFront ? 4 : 1;
    return mesh;
  }

  // ------------------------------------------------------------------ 3. ground layer
  private buildGround(): THREE.Mesh {
    const { m } = transparent();
    const p: U = positionLocal; // plane in XZ, corners +-0.5
    const S = this.groundS;
    m.vertexNode = cameraProjectionMatrix.mul(modelViewMatrix.mul(V4(FEET.add(V3(p.x.mul(S).mul(2 * M), 0.035, p.z.mul(S).mul(2 * M))), 1)));
    // world-oriented polar coords (metres) so the pattern doesn't spin with the hero's facing
    const lq = uv().sub(0.5).mul(S.mul(2));
    const q = vec2(lq.x, lq.y.negate());
    const r = length(q), th = atan(q.y, q.x);
    const tq = this.T; // held clock
    const fw = max(fwidth(r), 0.002);
    const R0 = this.W.mul(0.82).mul(this.throb).mul(min(this.grow, 1.1));
    // 1. lit pool with a flame-tongue edge
    const lobes = 9;
    const lob = fract(th.div(TAU).mul(lobes).add(tq.mul(0.35)));
    const li = floor(th.div(TAU).mul(lobes).add(tq.mul(0.35)));
    const spike = pow(oneMinus(abs(lob.mul(2).sub(1))), 2).mul(hash(li.add(floor(tq.mul(4)))).mul(0.16).add(0.05));
    const Rp = R0.mul(spike.add(0.95));
    const dp = Rp.sub(r);
    const poolIn = smoothstep(fw.negate(), fw, dp);
    const poolMid = smoothstep(0.16, 0.16 + 0.02, dp.div(R0));
    let col: U = mix(this.cLine, this.cOuter, smoothstep(0.035, 0.05, dp.div(R0)));
    col = mix(col, this.cMid.mul(this.emit), poolMid);
    let a: U = poolIn.mul(0.62);
    // 2. pressure streaks: dark dashes racing outward (grass flattened by the blast of energy)
    const N = 30;
    const sec = th.div(TAU).mul(N), si = floor(sec), sf = fract(sec);
    const jitter = hash(si.mul(1.3));
    const lineW = 0.09;
    const across = abs(sf.sub(0.5).sub(jitter.sub(0.5).mul(0.5)));
    const dash = smoothstep(0.42, 0.5, fract(r.mul(1.3).sub(tq.mul(2.4)).add(jitter.mul(3))));
    const streakZone = smoothstep(R0.mul(0.95), R0.mul(1.15), r).mul(oneMinus(smoothstep(R0.mul(1.7), R0.mul(2.7), r)));
    const streak = oneMinus(smoothstep(lineW * 0.5, lineW, across)).mul(dash).mul(streakZone).mul(step(0.35, jitter));
    col = mix(col, vec3(0.02, 0.05, 0.02), streak.mul(oneMinus(poolIn)));
    a = max(a, streak.mul(0.5));
    // 3. pressure / dust rings rolling out from the base
    const dust = vec3(0.72, 0.62, 0.45), dustD = vec3(0.36, 0.29, 0.2);
    for (let k = 0; k < 2; k++) {
      const kk = fract(time.mul(1.5).add(k * 0.5));
      const rr = R0.add(kk.mul(1.9)).mul(sin(th.mul(7).add(k * 3)).mul(0.05).add(1));
      const thick = oneMinus(kk).mul(0.22).add(0.03);
      const band = oneMinus(smoothstep(thick.mul(0.75), thick, abs(r.sub(rr))));
      const inner = smoothstep(rr.sub(thick), rr, r); // bright leading edge, darker trailing side
      col = mix(col, mix(dustD, dust, inner), band.mul(oneMinus(poolIn)));
      a = max(a, band.mul(oneMinus(kk)).mul(0.55));
    }
    // 4. ignition / level-up: shock ring + dust blast
    const kb = clamp(this.burst.div(0.45), 0, 1), kd = clamp(this.burst.div(0.85), 0, 1);
    const rs = oneMinus(pow(oneMinus(kb), 3)).mul(7).mul(this.burstR).add(0.6);
    const shock = oneMinus(smoothstep(0, oneMinus(kb).mul(0.32).add(0.06), abs(r.sub(rs)))).mul(oneMinus(kb));
    const rd = oneMinus(pow(oneMinus(kd), 2)).mul(4.2).mul(this.burstR).add(0.4).mul(sin(th.mul(11)).mul(0.07).add(1));
    const blast = oneMinus(smoothstep(0, oneMinus(kd).mul(0.8).add(0.05), abs(r.sub(rd)))).mul(oneMinus(kd));
    col = mix(col, mix(dustD, dust, smoothstep(rd.sub(0.4), rd, r)), blast.mul(this.burstK));
    a = max(a, blast.mul(0.9).mul(this.burstK));
    col = mix(col, mix(this.cHot.mul(this.emit.mul(1.4)), this.cGlow.mul(this.emit), kb), shock.mul(this.burstK));
    a = max(a, shock.mul(this.burstK));
    // 5. ground cracks (VII inferno; faint under VIII): jagged radial fissures glowing in the core colour, pulsing slowly
    { const cN = 11, cs = th.div(TAU).mul(cN), ci = floor(cs), cf = fract(cs), cj = hash(ci.mul(3.7).add(1.3));
      const cLen = R0.mul(1.2).add(cj.mul(2.4)), wob = sin(r.mul(5).add(cj.mul(20))).mul(0.07).add(sin(r.mul(13).add(cj.mul(7))).mul(0.03));
      const across = abs(cf.sub(0.5).add(wob)).mul(r.add(0.3)); // ~constant width in metres along the fissure
      const crack = oneMinus(smoothstep(0.02, 0.05, across)).mul(oneMinus(smoothstep(cLen.sub(0.4), cLen, r))).mul(smoothstep(R0.mul(0.25), R0.mul(0.55), r)).mul(step(0.3, cj));
      const glowK = sin(time.mul(2.6).add(cj.mul(9))).mul(0.3).add(0.7);
      col = mix(col, mix(vec3(0.03, 0.01, 0.01), this.cCore.mul(this.emit.mul(1.3)), glowK), crack.mul(this.cracks));
      a = max(a, crack.mul(this.cracks).mul(0.95)); }
    m.colorNode = col;
    m.opacityNode = a.mul(this.groundK);
    m.polygonOffset = true; m.polygonOffsetFactor = -2; m.polygonOffsetUnits = -2;
    const geo = new THREE.PlaneGeometry(1, 1, 1, 1); geo.rotateX(-Math.PI / 2);
    const mesh = new THREE.Mesh(geo, m);
    mesh.frustumCulled = false; mesh.renderOrder = 0;
    return mesh;
  }

  // ------------------------------------------------------------------ 4. lifting pebbles (opaque, one merged mesh)
  private buildRocks(): THREE.Mesh {
    const base = new THREE.OctahedronGeometry(1, 0).toNonIndexed();
    const bp = base.getAttribute('position'), bn = base.getAttribute('normal');
    const pos: number[] = [], nrm: number[] = [], info: number[] = [];
    for (let i = 0; i < ROCKS; i++) {
      const seed = (i * 0.618034 + 0.21) % 1;
      for (let k = 0; k < bp.count; k++) { pos.push(bp.getX(k), bp.getY(k), bp.getZ(k)); nrm.push(bn.getX(k), bn.getY(k), bn.getZ(k)); info.push(seed, i / ROCKS); }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
    geo.setAttribute('rock', new THREE.Float32BufferAttribute(info, 2));
    const m = new THREE.MeshBasicNodeMaterial({ fog: false });
    const a: U = attribute('rock', 'vec2'), seed = a.x, active = step(a.y, this.nRocks);
    const tq = floor(time.mul(12)).div(12); // on twos
    const L = seed.mul(1.1).add(1.3);
    const ph = tq.div(L).add(seed.mul(7.3)), pr = fract(ph), cyc = mod(floor(ph), 241);
    const th = hash(seed.mul(31.1).add(cyc)).mul(TAU);
    const rad = hash(seed.mul(17.3).add(cyc.mul(1.3))).mul(0.9).add(0.5).mul(this.W).mul(pr.mul(0.3).add(1));
    const hmax = hash(seed.mul(5.1).add(cyc.mul(0.7))).mul(1.1).add(0.3).mul(this.H.div(3.5));
    const fall = oneMinus(smoothstep(0.84, 1, pr));
    // VIII: the pebbles rise as a wide ring around the hero instead of lifting from the pool
    const rad2 = mix(rad, this.W.mul(1.5).add(pr.mul(0.5)).add(seed.mul(0.3)), this.ringK);
    const y = mix(hmax.mul(smoothstep(0, 0.78, pr)), hmax.mul(0.75).mul(pr), this.ringK).add(sin(tq.mul(7).add(seed.mul(20))).mul(0.03)).mul(fall).add(0.04);
    const sz = hash(seed.mul(9.7).add(cyc)).mul(0.1).add(0.05).mul(active).mul(smoothstep(0, 0.08, pr)).mul(oneMinus(smoothstep(0.93, 1, pr))).mul(min(this.alpha.mul(2), 1));
    const ang = tq.mul(seed.mul(3).add(2)).add(seed.mul(9)), c = cos(ang), s = sin(ang), c2 = cos(ang.mul(0.7)), s2 = sin(ang.mul(0.7));
    const rotY = (q: U): U => V3(q.x.mul(c).add(q.z.mul(s)), q.y, q.z.mul(c).sub(q.x.mul(s)));
    const rotX = (q: U): U => V3(q.x, q.y.mul(c2).sub(q.z.mul(s2)), q.y.mul(s2).add(q.z.mul(c2)));
    const lp = rotX(rotY((positionLocal as U).mul(vec3(1, 0.62, 0.84)).mul(sz)));
    const n: U = rotX(rotY(normalLocal));
    const centre = worldDir(th).mul(rad2).add(V3(0, y, 0));
    m.vertexNode = cameraProjectionMatrix.mul(modelViewMatrix.mul(V4(FEET.add(centre.add(lp).mul(M)), 1)));
    const nV = vary(n, 'vRockN');
    const lit = smoothstep(0.05, 0.2, nV.y.mul(0.8).add(nV.x.mul(0.35)));
    const under = smoothstep(0.2, 0.5, nV.y.negate());
    m.colorNode = mix(mix(vec3(0.09, 0.07, 0.055), vec3(0.36, 0.3, 0.23), lit), this.cMid.mul(0.8), under.mul(0.7));
    const mesh = new THREE.Mesh(geo, m);
    mesh.frustumCulled = false;
    return mesh;
  }

  // ------------------------------------------------------------------ 5. dust puffs (steady + ignition blast)
  private buildDust(): THREE.Mesh {
    const n = DUST + DUST_BURST;
    const pos: number[] = [], uvs: number[] = [], info: number[] = [], idx: number[] = [];
    for (let i = 0; i < n; i++) {
      const seed = (i * 0.618034 + 0.41) % 1, b = i * 4, burst = i >= DUST ? 1 : 0, k = burst ? (i - DUST) / DUST_BURST : i / DUST;
      for (const [cx, cy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) { pos.push(cx, cy, 0); uvs.push(cx * 0.5 + 0.5, cy * 0.5 + 0.5); info.push(seed, k, burst); }
      idx.push(b, b + 1, b + 2, b, b + 2, b + 3);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geo.setAttribute('dust', new THREE.Float32BufferAttribute(info, 3));
    geo.setIndex(idx);
    const { m } = transparent();
    const { right, up } = camFrame();
    const a: U = attribute('dust', 'vec3'), seed = a.x, k = a.y, isB = a.z;
    const tq = floor(time.mul(12)).div(12);
    // steady: rolling outward along the ground from the aura's base
    const L = seed.mul(0.4).add(0.8);
    const ph = tq.div(L).add(seed.mul(5.7)), pr = fract(ph), cyc = mod(floor(ph), 241);
    const thS = hash(seed.mul(13.1).add(cyc)).mul(TAU);
    const rS = pr.mul(1.4).add(0.5).mul(this.W);
    const yS = pr.mul(0.25).add(0.05);
    const szS = pr.mul(0.28).add(0.14).mul(hash(seed.mul(3.3).add(cyc)).mul(0.4).add(0.8));
    const aS = pow(oneMinus(pr), 1.3).mul(step(k, this.nDust)).mul(this.groundK).mul(smoothstep(0, 0.1, pr));
    // burst: a ring of puffs blown outward at ignition / level-up
    const kb = clamp(this.burst.div(0.75), 0, 1);
    const thB = k.mul(TAU).add(seed.mul(0.4));
    const rB = oneMinus(pow(oneMinus(kb), 2.5)).mul(3.8).add(0.5);
    const yB = kb.mul(0.25).add(0.08), szB = kb.mul(0.3).add(0.22);
    const aB = pow(oneMinus(kb), 2.5).mul(this.burstK).mul(step(this.burst, 0.75)).mul(0.8);
    const th = mix(thS, thB, isB), r = mix(rS, rB, isB), y = mix(yS, yB, isB), sz = mix(szS, szB, isB), alpha = mix(aS, aB, isB);
    const p: U = positionLocal;
    const centre = worldDir(th).mul(r).add(V3(0, y, 0));
    m.vertexNode = cameraProjectionMatrix.mul(modelViewMatrix.mul(V4(FEET.add(centre.mul(M)).add(right.mul(p.x.mul(sz).mul(M))).add(up.mul(p.y.mul(sz).mul(M))), 1)));
    const aV = vary(alpha, 'vDustA'), sV = vary(seed, 'vDustS');
    const q = uv().mul(2).sub(1), rr = length(q), an = atan(q.y, q.x);
    const edge = pow(abs(sin(an.mul(2.5).add(sV.mul(20)))), 0.5).mul(0.16).add(sin(an.mul(7).add(sV.mul(7))).mul(0.04)).add(0.66); // cauliflower lobes
    const fw = max(fwidth(rr), 0.01);
    const inside = smoothstep(edge, edge.sub(fw), rr);
    const shade = smoothstep(-0.15, 0.05, q.y.add(sin(q.x.mul(4).add(sV.mul(9))).mul(0.12)));
    const rim = smoothstep(edge.sub(0.12), edge.sub(0.12).add(fw), rr);
    let col: U = mix(vec3(0.46, 0.33, 0.19), vec3(0.86, 0.72, 0.5), shade);
    col = mix(col, vec3(0.3, 0.21, 0.12), rim.mul(0.8));
    m.colorNode = col;
    m.opacityNode = inside.mul(aV).mul(0.8);
    const mesh = new THREE.Mesh(geo, m);
    mesh.frustumCulled = false; mesh.renderOrder = 2;
    return mesh;
  }

  // ------------------------------------------------------------------ 6. speed lines (24 fps)
  private buildLines(): THREE.Mesh {
    const pos: number[] = [], uvs: number[] = [], info: number[] = [], idx: number[] = [];
    for (let i = 0; i < LINES; i++) {
      const seed = (i * 0.618034 + 0.137) % 1, b = i * 4;
      for (const [cx, cy] of [[-1, 0], [1, 0], [1, 1], [-1, 1]]) { pos.push(cx, cy, 0); uvs.push(cx * 0.5 + 0.5, cy); info.push(seed, i / LINES); }
      idx.push(b, b + 1, b + 2, b, b + 2, b + 3);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geo.setAttribute('sline', new THREE.Float32BufferAttribute(info, 2));
    geo.setIndex(idx);
    const { m } = transparent();
    const { right, up } = camFrame();
    const a: U = attribute('sline', 'vec2'), seed = a.x, active = step(a.y, this.nLines);
    const t24 = floor(time.mul(24)).div(24);
    const L = seed.mul(0.12).add(0.2);
    const ph = t24.div(L).add(seed.mul(9.1)), life = fract(ph), cyc = mod(floor(ph), 241);
    const th = hash(seed.mul(71.3).add(cyc.mul(0.73))).mul(TAU);
    const rad = hash(seed.mul(13.7).add(cyc)).mul(0.95).add(0.25).mul(this.W);
    const y0 = hash(seed.mul(3.3).add(cyc.mul(1.7))).mul(0.45).mul(this.H);
    const rise = hash(seed.mul(5.9).add(cyc)).mul(7).add(9).mul(life).mul(L);
    const len = hash(seed.mul(2.7).add(cyc.mul(0.4))).mul(1.1).add(0.6).mul(this.H.div(3.5));
    const w = hash(seed.mul(8.1).add(cyc)).mul(0.035).add(0.03).mul(active).mul(min(this.alpha.mul(2), 1));
    const A: U = normalize(mix(vec3(0, 1, 0), up, 0.5));
    const p: U = positionLocal;
    const base = V3(cos(th).mul(rad), y0.add(rise), sin(th).mul(rad));
    m.vertexNode = cameraProjectionMatrix.mul(modelViewMatrix.mul(V4(FEET.add(base.mul(M)).add(right.mul(p.x.mul(w).mul(M))).add(A.mul(p.y.mul(len).mul(M))), 1)));
    const lifeV = vary(life, 'vLineLife');
    const along = uv().y, across = abs(uv().x.mul(2).sub(1));
    const prof = pow(along.mul(oneMinus(along)).mul(4), 0.6); // tapered at both ends
    const fw = max(fwidth(across), 0.02);
    const inside = smoothstep(prof, prof.sub(fw), across);
    const core = smoothstep(prof.mul(0.45), prof.mul(0.45).sub(fw), across);
    m.colorNode = mix(mix(this.cGlow, this.cMid, this.rimK).mul(this.emit), mix(this.cHot, this.cGlow, this.rimK).mul(this.emit.mul(1.4)), core); // VII: black streaks, red cores
    m.opacityNode = inside.mul(smoothstep(0, 0.12, lifeV)).mul(oneMinus(smoothstep(0.7, 1, lifeV))).mul(0.95);
    const mesh = new THREE.Mesh(geo, m);
    mesh.frustumCulled = false; mesh.renderOrder = 5;
    return mesh;
  }

  // ------------------------------------------------------------------ 7. GPU lightning crawling over the body (15 fps)
  private buildBolts(): THREE.Mesh {
    const pos: number[] = [], uvs: number[] = [], info: number[] = [], idx: number[] = [];
    let vtx = 0;
    for (let i = 0; i < BOLTS; i++) {
      const seed = (i * 0.618034 + 0.29) % 1;
      for (let j = 0; j < BOLT_SEGS; j++) {
        for (const [e, s] of [[0, -1], [0, 1], [1, 1], [1, -1]]) { pos.push(s, 0, 0); uvs.push(e, s * 0.5 + 0.5); info.push(seed, i / BOLTS, j, e); }
        idx.push(vtx, vtx + 1, vtx + 2, vtx, vtx + 2, vtx + 3); vtx += 4;
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geo.setAttribute('bolt', new THREE.Float32BufferAttribute(info, 4));
    geo.setIndex(idx);
    const { m } = transparent();
    const { fullBack } = camFrame();
    const a: U = attribute('bolt', 'vec4'), seed = a.x, j = a.z, e = a.w;
    const cyc = mod(floor(time.mul(15).add(seed.mul(13))), 997);
    const vis = step(hash(cyc.mul(1.31).add(seed.mul(7))), this.duty).mul(step(a.y, this.nBolts));
    const th0 = hash(cyc.mul(2.7).add(seed.mul(3))).mul(TAU);
    const y0 = hash(cyc.mul(0.91).add(seed.mul(5))).mul(0.75).add(0.12).mul(1.8);
    const r0 = hash(cyc.mul(1.73).add(seed)).mul(0.22).add(0.3);
    const dir = step(0.5, hash(cyc.mul(3.1).add(seed.mul(2)))).mul(2).sub(1);
    const out = step(0.55, hash(cyc.mul(4.3).add(seed.mul(9)))).mul(this.lash.mul(0.7).add(0.13)); // some bolts arc outward off the body (IV: they lash out far)
    const pt = (k: U): U => {
      const th = th0.add(dir.mul(k).mul(0.3)).add(hash(cyc.mul(0.53).add(k.mul(3.7)).add(seed.mul(4))).sub(0.5).mul(0.25));
      const yy = y0.add(hash(cyc.add(k.mul(1.9)).add(seed.mul(11))).sub(0.5).mul(0.3)).add(k.mul(0.05));
      const rr = r0.add(k.mul(out)).add(hash(cyc.mul(0.37).add(k.mul(2.3)).add(seed)).sub(0.5).mul(0.14));
      return FEET.add(V3(cos(th).mul(rr), yy, sin(th).mul(rr)).mul(M));
    };
    const PA = pt(j), PB = pt(j.add(1));
    const side = cross(PB.sub(PA), fullBack);
    const p: U = positionLocal;
    const w = float(0.055 * M).mul(vis).mul(min(this.alpha.mul(2), 1)).mul(this.rimK.mul(0.5).add(1)); // VII: heavier bolts
    m.vertexNode = cameraProjectionMatrix.mul(modelViewMatrix.mul(V4(mix(PA, PB, e).add(normalize(side).mul(p.x.mul(w).mul(0.5))), 1)));
    const across = abs(uv().y.mul(2).sub(1));
    m.colorNode = mix(this.cHot.mul(this.emit.mul(1.8)), this.cBolt.mul(this.emit.mul(1.5)), smoothstep(0.25, 0.45, across));
    m.opacityNode = oneMinus(smoothstep(0.75, 1, across));
    const mesh = new THREE.Mesh(geo, m);
    mesh.frustumCulled = false; mesh.renderOrder = 6;
    return mesh;
  }

  // ------------------------------------------------------------------ 9. V eclipse: dark corona ring with a bent-light rim
  /** one camera-facing quad a little behind the hero: a near-black band whose outer rim refracts the aura colours into
   *  slowly turning bright streaks (a fake of light bending round the corona; no post pass) */
  private buildCorona(): THREE.Mesh {
    const { m } = transparent();
    const { right, up, fullBack } = camFrame();
    const p: U = positionLocal;
    const sz = this.W.mul(1.35).mul(min(this.grow, 1.1));
    const C = FEET.add(V3(0, 1.05 * M, 0)).add(fullBack.mul(-0.35 * M));
    m.vertexNode = cameraProjectionMatrix.mul(modelViewMatrix.mul(V4(C.add(right.mul(p.x.mul(sz).mul(M * 2))).add(up.mul(p.y.mul(sz).mul(M * 2.3))), 1)));
    const q = uv().mul(2).sub(1), r = length(q), an = atan(q.y, q.x);
    const R = sin(an.mul(5).add(time.mul(0.7))).mul(0.015).add(sin(an.mul(3).sub(time.mul(0.4))).mul(0.012)).add(0.74);
    const fw = max(fwidth(r), 0.004);
    const band = smoothstep(R.sub(0.2), R.sub(0.14), r).mul(oneMinus(smoothstep(R.add(0.01), R.add(0.01).add(fw), r)));
    const rim = smoothstep(R, R.add(fw.mul(2)), r).mul(oneMinus(smoothstep(R.add(0.05), R.add(0.1), r)));
    // refraction look: three bright lobes travelling round the rim, each splitting core -> mid -> outer colour radially
    const lobe = pow(sin(fract(an.div(TAU).mul(3).add(time.mul(0.11))).mul(Math.PI)), 6);
    const split = smoothstep(R.add(0.02), R.add(0.08), r);
    const rimCol = mix(mix(this.cCore.mul(this.emit.mul(1.5)), this.cMid.mul(this.emit.mul(1.2)), split), this.cOuter.mul(this.emit), split.mul(0.5)).mul(lobe.mul(0.8).add(0.35));
    // thin bent-light threads inside the dark band
    const thread = pow(abs(sin(an.mul(19).add(time.mul(1.3)).add(r.mul(40)))), 24).mul(smoothstep(R.sub(0.17), R.sub(0.1), r)).mul(oneMinus(smoothstep(R.sub(0.04), R, r)));
    const col = mix(vec3(0.02, 0.0, 0.03), rimCol, rim).add(this.cMid.mul(thread.mul(0.6)));
    m.colorNode = col;
    m.opacityNode = band.mul(0.88).add(rim).add(thread.mul(0.5)).mul(this.coronaK).mul(this.alpha);
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1, 1, 1), m);
    mesh.frustumCulled = false; mesh.renderOrder = 0; mesh.visible = false;
    return mesh;
  }

  // ------------------------------------------------------------------ 10. VIII shards: crystal shards orbiting the body
  private buildCrystals(): THREE.Mesh {
    const base = new THREE.OctahedronGeometry(1, 0).toNonIndexed();
    const bp = base.getAttribute('position'), bn = base.getAttribute('normal');
    const pos: number[] = [], nrm: number[] = [], info: number[] = [];
    for (let i = 0; i < CRYSTALS; i++) {
      const seed = (i * 0.618034 + 0.07) % 1;
      for (let k = 0; k < bp.count; k++) { pos.push(bp.getX(k), bp.getY(k), bp.getZ(k)); nrm.push(bn.getX(k), bn.getY(k), bn.getZ(k)); info.push(seed, i / CRYSTALS); }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
    geo.setAttribute('crys', new THREE.Float32BufferAttribute(info, 2));
    const m = new THREE.MeshBasicNodeMaterial({ fog: false });
    const a: U = attribute('crys', 'vec2'), seed = a.x, active = step(a.y, this.nCrystals);
    const dir = step(0.5, fract(seed.mul(7.3))).mul(2).sub(1);
    const th = seed.mul(TAU).add(time.mul(seed.mul(0.5).add(0.55)).mul(dir));
    const rad = this.W.mul(seed.mul(0.6).add(0.85));
    const y = seed.mul(1.4).add(0.45).add(sin(time.mul(1.4).add(seed.mul(9))).mul(0.14));
    const sz = fract(seed.mul(3.1)).mul(0.07).add(0.05).mul(active).mul(min(this.alpha.mul(2), 1));
    const ang = time.mul(seed.mul(2).add(1.5)).add(seed.mul(9)), c = cos(ang), s = sin(ang), c2 = cos(ang.mul(0.6)), s2 = sin(ang.mul(0.6));
    const rotY = (q: U): U => V3(q.x.mul(c).add(q.z.mul(s)), q.y, q.z.mul(c).sub(q.x.mul(s)));
    const rotX = (q: U): U => V3(q.x, q.y.mul(c2).sub(q.z.mul(s2)), q.y.mul(s2).add(q.z.mul(c2)));
    const lp = rotX(rotY((positionLocal as U).mul(vec3(0.5, 1.7, 0.5)).mul(sz)));
    const n: U = rotX(rotY(normalLocal));
    const centre = worldDir(th).mul(rad).add(V3(0, y, 0));
    m.vertexNode = cameraProjectionMatrix.mul(modelViewMatrix.mul(V4(FEET.add(centre.add(lp).mul(M)), 1)));
    const nV = vary(n, 'vCrysN');
    const lit = smoothstep(-0.2, 0.6, nV.y.mul(0.7).add(nV.x.mul(0.5)));
    m.colorNode = mix(this.cBolt.mul(0.9), this.cCore.mul(this.emit.mul(1.6)), lit);
    const mesh = new THREE.Mesh(geo, m);
    mesh.frustumCulled = false; mesh.visible = false;
    return mesh;
  }

  // ------------------------------------------------------------------ 11. VIII shards: fracture lines in the air (15 fps)
  /** short bright cracks that appear and vanish around the hero, each with a side branch; the air itself fracturing */
  private buildFracts(): THREE.Mesh {
    const pos: number[] = [], uvs: number[] = [], info: number[] = [], idx: number[] = [];
    let vtx = 0;
    for (let i = 0; i < FRACTS; i++) {
      const seed = (i * 0.618034 + 0.53) % 1;
      for (let j = 0; j < 2; j++) { // main line + branch
        for (const [e, sd] of [[0, -1], [0, 1], [1, 1], [1, -1]]) { pos.push(sd, 0, 0); uvs.push(e, sd * 0.5 + 0.5); info.push(seed, i / FRACTS, j, e); }
        idx.push(vtx, vtx + 1, vtx + 2, vtx, vtx + 2, vtx + 3); vtx += 4;
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geo.setAttribute('frInfo', new THREE.Float32BufferAttribute(info, 4));
    geo.setIndex(idx);
    const { m } = transparent();
    const { right, up } = camFrame();
    const a: U = attribute('frInfo', 'vec4'), seed = a.x, j = a.z, e = a.w;
    const cyc = mod(floor(time.mul(15).mul(seed.mul(0.3).add(0.5)).add(seed.mul(17))), 997);
    const vis = step(hash(cyc.mul(1.7).add(seed.mul(5))), float(0.3)).mul(step(a.y, this.nFract));
    const th = hash(cyc.mul(2.3).add(seed)).mul(TAU), rr = hash(cyc.mul(1.1).add(seed.mul(3))).mul(0.9).add(0.6).mul(this.W);
    const y0 = hash(cyc.mul(0.7).add(seed.mul(9))).mul(1.9).add(0.3);
    const len = hash(cyc.mul(3.3).add(seed.mul(2))).mul(0.3).add(0.15).mul(mix(1, 0.45, j)); // branch: shorter
    const ang = hash(cyc.mul(0.9).add(seed.mul(4))).mul(TAU).add(j.mul(0.9));
    const C = FEET.add(V3(cos(th).mul(rr), y0, sin(th).mul(rr)).mul(M));
    const along: U = right.mul(cos(ang)).add(up.mul(sin(ang))), side: U = right.mul(sin(ang).negate()).add(up.mul(cos(ang)));
    const start = C.add(along.mul(j.mul(len).mul(0.35 * M))); // the branch leaves from a third along the main line
    const p: U = positionLocal;
    const w = float(0.022 * M).mul(vis).mul(min(this.alpha.mul(2), 1));
    m.vertexNode = cameraProjectionMatrix.mul(modelViewMatrix.mul(V4(start.add(along.mul(e.sub(0.5).mul(len).mul(M))).add(side.mul(p.x.mul(w))), 1)));
    const across = abs(uv().y.mul(2).sub(1)), tip = abs(uv().x.mul(2).sub(1));
    m.colorNode = mix(this.cCore.mul(this.emit.mul(2)), this.cBolt.mul(this.emit.mul(1.4)), smoothstep(0.2, 0.5, across));
    m.opacityNode = oneMinus(smoothstep(0.6, 1, across)).mul(oneMinus(smoothstep(0.7, 1, tip)));
    const mesh = new THREE.Mesh(geo, m);
    mesh.frustumCulled = false; mesh.renderOrder = 6; mesh.visible = false;
    return mesh;
  }

  // ------------------------------------------------------------------ 8. ignition / level-up flash (starburst + pillar)
  private buildFlash(): THREE.Mesh {
    const { m } = transparent(false);
    const { right, up } = camFrame();
    const p: U = positionLocal;
    const kb = clamp(this.burst.div(0.1), 0, 1);
    const sz = oneMinus(pow(oneMinus(kb), 3)).mul(this.burstK).mul(2.6).mul(this.burstR);
    const C = FEET.add(V3(0, 1.0 * M, 0));
    m.vertexNode = cameraProjectionMatrix.mul(modelViewMatrix.mul(V4(C.add(right.mul(p.x.mul(sz).mul(M))).add(up.mul(p.y.mul(sz).mul(M * 1.6))), 1)));
    const q = uv().mul(2).sub(1), r = length(q), an = atan(q.y, q.x);
    const rays = pow(abs(cos(an.mul(3).add(0.52))), 22).mul(0.72).add(0.24);
    const pillar = oneMinus(smoothstep(0.1, 0.16, abs(q.x))).mul(oneMinus(smoothstep(0.6, 1, abs(q.y))));
    const fw = max(fwidth(r), 0.01);
    const star = max(smoothstep(rays, rays.sub(fw), r), pillar);
    const core = smoothstep(rays.mul(0.55), rays.mul(0.55).sub(fw), r);
    const fade = oneMinus(clamp(this.burst.sub(0.05).div(0.22), 0, 1));
    m.colorNode = mix(this.cGlow.mul(this.emit.mul(1.6)), this.cHot.mul(this.emit.mul(2.2)), max(core, pillar));
    m.opacityNode = star.mul(fade);
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1, 1, 1), m);
    mesh.frustumCulled = false; mesh.renderOrder = 7; mesh.visible = false;
    return mesh;
  }

  // ------------------------------------------------------------------ per frame (scalar uniform writes only)
  /** A/B: the levels either side of the smoothed tier, k the blend; env: the 0..1 Surge envelope */
  update(tierF: number, env: number, dt: number, glowOn: boolean, extra: number, reduced: boolean): void {
    // ignition (0 -> I) and level-up flares fire as soon as the smoothed tier starts moving toward the new level
    const lvl = tierF < 0.02 ? 0 : Math.min(V3_TOP, Math.floor(tierF + 0.9));
    if (lvl > this.lvl) { this.burstT = 0; this.burstMode = this.lvl === 0 ? 1 : 2; if (this.lvl === 0) this.lv = lvl; this.burstGain = FLASH.big(); }
    this.lvl = lvl;
    // the v3 look follows the level it is heading to (snappy 0.1 s cross-fade, no slow wash through the base palette);
    // on the way out it keeps the last level's look while it fades and shrinks
    if (lvl > 0) this.lv += (lvl - this.lv) * Math.min(1, dt * 10);
    const lv = Math.max(1, this.lv), i = Math.min(V3_TOP - 1, Math.floor(lv)), k = Math.min(1, lv - i), A = V3_LOOKS[i], B = V3_LOOKS[i + 1];
    const n = (x: number, y: number) => x + (y - x) * k;
    // colours pop to the new level on the flare frame (a hue cross-fade red -> blue would pass through purple)
    const Pi = Math.min(V3_TOP, Math.max(1, Math.round(lv + 0.45))), P = V3_LOOKS[Pi];
    this.intensity = n(SURGE_FORMS[i - 1]?.intensity ?? 0, SURGE_FORMS[i]?.intensity ?? 0) * Math.min(1, env * 3);
    (this.cLine.value as THREE.Color).setHex(P.line); (this.cOuter.value as THREE.Color).setHex(P.outer);
    (this.cCore.value as THREE.Color).setHex(P.core); (this.cMid.value as THREE.Color).setHex(P.mid); (this.cBolt.value as THREE.Color).setHex(P.bolt);
    (this.cHot.value as THREE.Color).setHex(P.hot ?? P.core); (this.cGlow.value as THREE.Color).setHex(P.glow ?? P.mid);
    // VII abyss rim: a slow heavy throb in its glow (gravitational pulse; held still under Reduce flashing)
    this.rimK.value = P.rim * (reduced ? 1 : 0.88 + 0.12 * Math.sin(this.clock * 2.4));
    { const I = SURGE_FORMS[Pi - 1]?.intensity ?? 0; // wrap-around rim light + halo, scaled by the form
      this.wrapK.value = 0.55 + 0.45 * I; this.wrapW.value = 0.028 + 0.03 * I;
      this.haloK.value = (0.16 + 0.3 * I + 0.3 * P.rim) * (glowOn ? 1 : 0.8); this.haloW.value = 0.055 + 0.07 * I + 0.03 * P.rim; }
    // motif layers follow the target form (they pop with the colours); their counts fade with the envelope
    this.lash.value = P.lash; this.coronaK.value = P.corona * env; this.heat.value = P.heat * (reduced ? FLASH.L.flicker : 1); this.cracks.value = P.cracks * env;
    this.nCrystals.value = P.crystals * env * (0.5 + 0.5 * this.qp); this.nFract.value = P.fract * env * (reduced ? 0.5 : 1) * (0.5 + 0.5 * this.qp); this.ringK.value = P.ringK;
    this.corona.visible = P.corona > 0 && env > 0.01; this.crystals.visible = P.crystals > 0 && env > 0.01; this.fracts.visible = P.fract > 0 && env > 0.01;
    this.burstR.value = P.burst;
    this.burstT = Math.min(9, this.burstT + dt);
    const b = this.burstT;
    let g = 1;
    if (this.burstMode === 1 && b < 0.4) { const x = Math.min(1, b / 0.26); g = 1 + 2.4 * Math.pow(x - 1, 3) + 1.4 * Math.pow(x - 1, 2); } // ease-out-back from the feet
    else if (this.burstMode === 2 && b < 0.4) g = 1 + 0.22 * Math.sin(Math.PI * Math.min(1, b / 0.4));
    this.burst.value = b; this.burstK.value = (this.burstMode === 1 ? 1 : 0.75) * (0.6 + 0.4 * P.burst) * (reduced ? this.burstGain : 1);
    this.grow.value = Math.max(0, g) * (0.35 + 0.65 * env);
    this.flash.visible = b < 0.3 && this.burstK.value > 0.001;
    // stage alpha snaps in (the growth from the feet is the entrance), fades out with the envelope
    this.alpha.value = Math.min(1, env * 3);
    this.emit.value = glowOn ? n(A.emitOn, B.emitOn) : n(A.emitOff, B.emitOff);
    this.H.value = n(A.H, B.H) * (1 + extra * 0.08); this.W.value = n(A.W, B.W) * (1 + extra * 0.05);
    this.sharp.value = n(A.sharp, B.sharp); this.wild.value = n(A.wild, B.wild);
    const qp = this.qp;
    this.nLines.value = n(A.lines, B.lines) * env * (0.5 + 0.5 * qp); this.nBolts.value = n(A.bolts, B.bolts) * env * (0.4 + 0.6 * qp);
    this.duty.value = n(A.duty, B.duty) * (reduced ? 0.6 : 1); this.nRocks.value = n(A.rocks, B.rocks) * env * qp; this.nDust.value = n(A.dust, B.dust) * qp;
    this.groundK.value = n(A.ground, B.ground) * Math.min(1, env * 2);
    this.groundS.value = Math.max(2.9 * n(A.W, B.W), b < 0.9 ? 8.2 * P.burst : 0);
    // held poses: the flame clock advances smoothly but the GPU only sees it 12x a second
    this.clock += dt * n(A.rate, B.rate) * (1 + extra * 0.3);
    this.holdAcc += dt;
    if (this.holdAcc >= 1 / 12) {
      this.holdAcc %= 1 / 12;
      this.T.value = this.clock;
      const ph = (this.clock * 1.5) % 1; // sharp-attack throb
      this.throb.value = 1 + n(A.throb, B.throb) * Math.pow(1 - ph, 4) * (reduced ? FLASH.L.flicker : 1);
    }
  }
}
